import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, ExternalLink, FileText, MessageSquare, Sparkles } from "lucide-react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";

/**
 * B5.5 + C3.1 — Notifications popover.
 *
 * Bell icon with a small dot when unread > 0. Click abre el popover y consume
 * `GET /v2/notifications/me`. Si la query falla cae a feed vacío (sin mocks).
 */
export function NotificationsPopover() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const notifsQuery = useQuery({
    queryKey: ["notifications", "me"],
    queryFn: () => api.listNotifications({ limit: 30 }).catch(() => ({ items: [] })),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const feed = notifsQuery.data?.items ?? [];
  const unread = feed.filter((n) => n.read_at == null).length;

  const markAllRead = useMutation({
    mutationFn: () => api.markNotificationsRead({ all: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", "me"] });
    },
    onError: () => {
      /* silent */
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notificaciones"
          data-testid="notifications-toggle"
          data-unread={unread}
          className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-(--text-secondary) hover:bg-(--bg-hover) hover:text-(--text-primary)"
        >
          <Bell size={16} />
          {unread > 0 && (
            <span
              data-testid="notifications-dot"
              className="absolute top-1.5 right-1.5 grid h-2 w-2 place-items-center rounded-full bg-(--accent-500)"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-80 p-0"
        data-testid="notifications-popover"
      >
        <header className="flex items-center justify-between border-b border-(--border-subtle) px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-(--text-tertiary)">
            Notificaciones
          </span>
          <button
            type="button"
            data-testid="notifications-mark-all"
            onClick={() => markAllRead.mutate()}
            disabled={unread === 0 || markAllRead.isPending}
            className="text-[10px] text-(--text-tertiary) hover:text-(--text-primary) disabled:opacity-40"
          >
            marcar todas
          </button>
        </header>
        <ul
          data-testid="notifications-list"
          className="max-h-[420px] overflow-y-auto divide-y divide-(--border-subtle)"
        >
          {notifsQuery.isLoading && (
            <li className="px-3 py-3 text-xs text-(--text-tertiary)">Cargando…</li>
          )}
          {!notifsQuery.isLoading && feed.length === 0 && (
            <li className="flex items-center justify-center gap-1.5 px-3 py-6 text-xs text-(--text-tertiary)">
              <BellOff size={12} /> Sin notificaciones
            </li>
          )}
          {feed.map((n) => {
            const isUnread = n.read_at == null;
            const Icon =
              n.kind === "mention"
                ? MessageSquare
                : n.kind === "job_complete"
                  ? ExternalLink
                  : n.kind === "autolink_new"
                    ? FileText
                    : Sparkles;
            return (
              <li
                key={n.id}
                className={`flex items-start gap-2 px-3 py-2 text-sm ${
                  isUnread ? "bg-(--accent-50)" : ""
                }`}
                data-testid="notifications-item"
                data-unread={isUnread ? "true" : "false"}
              >
                <span className="mt-1 text-(--text-tertiary)">
                  <Icon size={12} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs leading-snug font-medium">{n.title}</p>
                  {n.body && (
                    <p className="mt-0.5 text-[11px] text-(--text-secondary) line-clamp-2">
                      {n.body}
                    </p>
                  )}
                  <p className="mt-0.5 font-mono text-[10px] text-(--text-tertiary)">
                    {formatRelative(n.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
